import { useEffect, useMemo, useRef, useState } from "react";
import { APIProvider, Map, AdvancedMarker, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { Bike, Utensils, Home as HomeIcon, Navigation2 } from "lucide-react";
import MapMock from "@/components/customer/MapMock";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Fallback map center — Grand Anse, Grenada.
const GRENADA_CENTER = { lat: 12.032, lng: -61.75 };

/**
 * DriverDeliveryMap
 * Renders a live Google Map for the driver showing:
 *  - Pickup marker (restaurant)
 *  - Dropoff marker (customer)
 *  - Animated driver marker moving from pickup -> dropoff
 *  - Polyline connecting the two points
 *
 * Falls back to the animated MapMock if the API key isn't configured or if
 * geocoding fails.
 */
export default function DriverDeliveryMap({
  pickupAddress,
  dropoffAddress,
  pickupCoords,   // { lat, lng } from the stored order (preferred)
  dropoffCoords,  // { lat, lng } from the stored order (preferred)
  driverPosition, // { lat, lng } from the server (customer view uses this)
  useDriverGps = true, // opt into navigator.geolocation for the live driver marker
  driverName = "You",
  height = "100%",
  dark = true,
}) {
  if (!GOOGLE_MAPS_API_KEY) {
    return <MapMock height={height} dark={dark} driverName={driverName} />;
  }
  return (
    <div className="w-full h-full" data-testid="driver-google-map">
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <DeliveryMapInner
          pickupAddress={pickupAddress}
          dropoffAddress={dropoffAddress}
          pickupCoords={pickupCoords}
          dropoffCoords={dropoffCoords}
          driverPosition={driverPosition}
          useDriverGps={useDriverGps}
          driverName={driverName}
          dark={dark}
          height={height}
        />
      </APIProvider>
    </div>
  );
}

function DeliveryMapInner({ pickupAddress, dropoffAddress, pickupCoords, dropoffCoords, driverPosition, useDriverGps, driverName, dark, height }) {
  // Use the Places library only when stored coordinates aren't available.
  const placesLib = useMapsLibrary("places");
  const [pickup, setPickup] = useState(() => (pickupCoords?.lat ? pickupCoords : null));
  const [dropoff, setDropoff] = useState(() => (dropoffCoords?.lat ? dropoffCoords : null));
  const [failed, setFailed] = useState(false);
  const [driverGps, setDriverGps] = useState(null);

  useEffect(() => {
    // Skip the Places lookup entirely when both endpoints already have stored coords.
    const needPickup = !pickup;
    const needDropoff = !dropoff;
    if (!needPickup && !needDropoff) return;
    if (!placesLib) return;
    let cancelled = false;
    // A hidden DIV is required to instantiate the PlacesService.
    const host = document.createElement("div");
    const service = new placesLib.PlacesService(host);
    const findPlace = (address) => new Promise((resolve) => {
      if (!address) return resolve(null);
      // Bias searches toward Grenada by appending country if missing.
      const q = /grenada/i.test(address) ? address : `${address}, Grenada`;
      service.findPlaceFromQuery(
        { query: q, fields: ["geometry.location", "name"] },
        (results, status) => {
          if (status === placesLib.PlacesServiceStatus.OK && results?.[0]?.geometry?.location) {
            const loc = results[0].geometry.location;
            resolve({ lat: loc.lat(), lng: loc.lng() });
          } else {
            resolve(null);
          }
        },
      );
    });
    (async () => {
      const [p, d] = await Promise.all([
        needPickup ? findPlace(pickupAddress) : Promise.resolve(pickup),
        needDropoff ? findPlace(dropoffAddress) : Promise.resolve(dropoff),
      ]);
      if (cancelled) return;
      // Fall back to offset points near Grenada if either lookup misses.
      const finalP = p || { lat: GRENADA_CENTER.lat + 0.005, lng: GRENADA_CENTER.lng - 0.005 };
      const finalD = d || { lat: GRENADA_CENTER.lat - 0.005, lng: GRENADA_CENTER.lng + 0.008 };
      setPickup(finalP);
      setDropoff(finalD);
      if (needPickup && needDropoff && !p && !d) setFailed(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [placesLib, pickupAddress, dropoffAddress]);

  // Watch the driver's real GPS position (when the browser grants permission).
  // Falls back silently to the animated marker between pickup and dropoff.
  useEffect(() => {
    if (driverPosition) return; // external position (customer view) — skip browser geolocation
    if (!useDriverGps || typeof navigator === "undefined" || !navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => setDriverGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setDriverGps(null),
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 },
    );
    return () => { try { navigator.geolocation.clearWatch(watchId); } catch { /* noop */ } };
  }, [useDriverGps, driverPosition]);

  // The effective driver marker: prefer external (server-broadcast) position,
  // fall back to the browser's own GPS, else the animated interpolation.
  const effectiveDriverPos = driverPosition || driverGps;

  const center = useMemo(() => {
    if (pickup && dropoff) {
      return { lat: (pickup.lat + dropoff.lat) / 2, lng: (pickup.lng + dropoff.lng) / 2 };
    }
    return GRENADA_CENTER;
  }, [pickup, dropoff]);

  if (!pickup || !dropoff) {
    // Still loading — show a subtle placeholder consistent with the dark theme.
    return (
      <div className="w-full h-full flex items-center justify-center bg-eatup-dark-surface" style={{ height }}>
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Loading map…</div>
      </div>
    );
  }

  return (
    <Map
      style={{ width: "100%", height: typeof height === "number" ? `${height}px` : height }}
      defaultCenter={center}
      defaultZoom={14}
      mapId="eats-gnd-driver"
      gestureHandling="greedy"
      disableDefaultUI={true}
      zoomControl={true}
      colorScheme={dark ? "DARK" : "LIGHT"}
      data-testid="driver-map"
    >
      <PickupMarker position={pickup} />
      <DropoffMarker position={dropoff} />
      <AnimatedDriverMarker pickup={pickup} dropoff={dropoff} driverGps={effectiveDriverPos} label={driverName} />
      <RoutePolyline pickup={pickup} dropoff={dropoff} />
      <FitBoundsOnLoad pickup={pickup} dropoff={dropoff} driverGps={effectiveDriverPos} />
      {effectiveDriverPos && (
        <div className="pointer-events-none absolute bottom-2 right-2 bg-eatup-green/90 text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live GPS
        </div>
      )}
      {failed && !effectiveDriverPos && (
        <div className="pointer-events-none absolute bottom-2 left-2 bg-black/60 text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full">
          Simulated route
        </div>
      )}
    </Map>
  );
}

function PickupMarker({ position }) {
  return (
    <AdvancedMarker position={position} title="Pickup">
      <div className="relative -translate-y-2">
        <div className="w-9 h-9 rounded-full bg-eatup-yellow border-2 border-white shadow-lg flex items-center justify-center">
          <Utensils className="w-4 h-4 text-eatup-charcoal" strokeWidth={2.5} />
        </div>
        <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-black/70 text-white">
          Pickup
        </div>
      </div>
    </AdvancedMarker>
  );
}

function DropoffMarker({ position }) {
  return (
    <AdvancedMarker position={position} title="Dropoff">
      <div className="relative -translate-y-2">
        <div className="w-9 h-9 rounded-full bg-eatup-green border-2 border-white shadow-lg flex items-center justify-center">
          <HomeIcon className="w-4 h-4 text-white" strokeWidth={2.5} />
        </div>
        <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-black/70 text-white">
          Dropoff
        </div>
      </div>
    </AdvancedMarker>
  );
}

function AnimatedDriverMarker({ pickup, dropoff, driverGps, label }) {
  const [t, setT] = useState(0);
  const [displayPos, setDisplayPos] = useState(driverGps || null);
  const [heading, setHeading] = useState(null); // degrees, 0=north, 90=east
  const prevGpsRef = useRef(null);
  const animRef = useRef(null);

  // Fallback animation between pickup->dropoff when no real GPS.
  useEffect(() => {
    if (driverGps) return;
    const id = setInterval(() => setT((v) => (v + 0.01) % 1), 200);
    return () => clearInterval(id);
  }, [driverGps]);

  // Smoothly interpolate the marker between successive GPS pings so the
  // scooter appears to *travel* on the map instead of teleporting every
  // time the customer's tracking-poll fetches a new location (~4s cadence).
  useEffect(() => {
    if (!driverGps) return;
    const prev = prevGpsRef.current;
    // First fix — just snap so the marker appears immediately.
    if (!prev) {
      prevGpsRef.current = driverGps;
      setDisplayPos(driverGps);
      return;
    }
    // Skip work if the driver didn't move (poll refetched the same coords).
    if (prev.lat === driverGps.lat && prev.lng === driverGps.lng) return;

    // Update heading (bearing) so the icon can face the direction of travel.
    setHeading(bearingBetween(prev, driverGps));

    // Animate over 3.5s using requestAnimationFrame → matches the poll interval.
    const DURATION = 3500;
    const start = performance.now();
    // Interpolate from wherever the marker is CURRENTLY displayed (not the
    // previous fully-settled position) so a mid-animation GPS ping doesn't
    // snap the marker backwards before easing forward again.
    const from = displayPos || prev;
    if (animRef.current) cancelAnimationFrame(animRef.current);
    const step = (now) => {
      const p = Math.min(1, (now - start) / DURATION);
      // Smooth ease-out so the marker glides in.
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayPos({
        lat: from.lat + (driverGps.lat - from.lat) * eased,
        lng: from.lng + (driverGps.lng - from.lng) * eased,
      });
      if (p < 1) {
        animRef.current = requestAnimationFrame(step);
      } else {
        animRef.current = null;
      }
    };
    // Always advance the "previous" ref immediately so heading calc on the
    // NEXT ping uses this ping's coords, even if animation is still running.
    prevGpsRef.current = driverGps;
    animRef.current = requestAnimationFrame(step);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [driverGps]);

  const pos = useMemo(() => {
    if (driverGps) return displayPos || driverGps;
    return {
      lat: pickup.lat + (dropoff.lat - pickup.lat) * t,
      lng: pickup.lng + (dropoff.lng - pickup.lng) * t,
    };
  }, [pickup, dropoff, t, driverGps, displayPos]);

  return (
    <AdvancedMarker position={pos} title={label} zIndex={10}>
      <div className="relative" data-testid="driver-marker">
        <span className="absolute inset-0 rounded-full bg-eatup-green/40 animate-ping" />
        <div
          className="relative w-11 h-11 rounded-full bg-eatup-green border-[2.5px] border-white shadow-xl flex items-center justify-center transition-transform duration-500"
          style={{ transform: heading != null ? `rotate(${heading}deg)` : undefined }}
        >
          {/* Navigation arrow points "up" by default → rotates with heading */}
          <Navigation2 className="w-5 h-5 text-white" strokeWidth={2.5} fill="currentColor" />
        </div>
      </div>
    </AdvancedMarker>
  );
}

// Great-circle bearing (initial course) from `from` → `to`, in degrees clockwise from north.
function bearingBetween(from, to) {
  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;
  const lat1 = toRad(from.lat), lat2 = toRad(to.lat);
  const dLng = toRad(to.lng - from.lng);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

function RoutePolyline({ pickup, dropoff }) {
  const map = useMap();
  const lineRef = useRef(null);
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    lineRef.current?.setMap(null);
    lineRef.current = new window.google.maps.Polyline({
      path: [pickup, dropoff],
      geodesic: true,
      strokeColor: "#18B45B",
      strokeOpacity: 0.9,
      strokeWeight: 4,
      icons: [{
        icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 3 },
        offset: "0",
        repeat: "16px",
      }],
      map,
    });
    return () => { lineRef.current?.setMap(null); };
  }, [map, pickup, dropoff]);
  return null;
}

function FitBoundsOnLoad({ pickup, dropoff, driverGps }) {
  const map = useMap();
  const hasFittedOnceRef = useRef(false);
  const lastRefitAtRef = useRef(0);

  // Initial fit — as soon as pickup + dropoff resolve.
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    if (hasFittedOnceRef.current) return;
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(pickup);
    bounds.extend(dropoff);
    if (driverGps) bounds.extend(driverGps);
    map.fitBounds(bounds, 80);
    hasFittedOnceRef.current = true;
    lastRefitAtRef.current = Date.now();
  }, [map, pickup, dropoff, driverGps]);

  // Re-fit intelligently when the driver drifts off-screen OR at most every
  // 20s. Prevents jittery re-centering on every 4-second GPS ping.
  useEffect(() => {
    if (!map || !driverGps || !window.google?.maps) return;
    if (!hasFittedOnceRef.current) return;
    const REFIT_COOLDOWN_MS = 20_000;
    const now = Date.now();
    const viewport = map.getBounds();
    const driverLatLng = new window.google.maps.LatLng(driverGps.lat, driverGps.lng);
    const dropoffLatLng = new window.google.maps.LatLng(dropoff.lat, dropoff.lng);
    const driverInView = viewport && viewport.contains(driverLatLng);
    const dropoffInView = viewport && viewport.contains(dropoffLatLng);
    const timeSinceLast = now - lastRefitAtRef.current;

    const needsRefit = !driverInView || !dropoffInView || timeSinceLast > REFIT_COOLDOWN_MS;
    if (!needsRefit) return;

    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(dropoff);
    bounds.extend(driverGps);
    // Include pickup only if it's still relevant (driver very close to it).
    map.fitBounds(bounds, 100);
    lastRefitAtRef.current = now;
  }, [map, driverGps, dropoff]);

  return null;
}
